import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnalyticsDashboard, DashboardMetrics } from '@/api/analytics';
import { MetricCard } from '@/components/analytics/MetricCard';
import { ActionTrendChart } from '@/components/analytics/ActionTrendChart';
import { ActionAgeChart } from '@/components/analytics/ActionAgeChart';
import { MeetingFrequencyChart } from '@/components/analytics/MeetingFrequencyChart';
import { InsightsPanel } from '@/components/analytics/InsightsPanel';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, CalendarDays } from 'lucide-react';

type Period = '30d' | '90d' | 'all';

export default function Analytics() {
  const [period, setPeriod] = useState<Period>('90d');
  const [includeShared, setIncludeShared] = useState(true);
  const [data, setData] = useState<DashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      setAccessDenied(false);
      try {
        const result = await getAnalyticsDashboard(period, includeShared);
        setData(result);
      } catch (err: any) {
        // Check if it's an access denied error (403)
        if (err.response?.status === 403) {
          setAccessDenied(true);
        } else {
          setError(err instanceof Error ? err : new Error('Failed to load analytics data'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [period, includeShared]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Redirect to subscription page if access denied
  if (accessDenied) {
    navigate('/app/subscription-choice');
    return null;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Error Loading Analytics</h2>
          <p className="text-muted-foreground">
            {error instanceof Error ? error.message : 'Failed to load analytics data'}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { actionMetrics, meetingMetrics, insights } = data;

  // Calculate trends
  const monthDiff = meetingMetrics.meetingsThisMonth - meetingMetrics.meetingsLastMonth;
  const monthTrendLabel = monthDiff > 0 
    ? `+${monthDiff} from last month`
    : monthDiff < 0
    ? `${monthDiff} from last month`
    : 'Same as last month';

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 dark:from-indigo-400 dark:via-violet-400 dark:to-purple-400 bg-clip-text text-transparent">
            Analytics
          </h1>
          <p className="text-lg text-muted-foreground mt-1">
            Insights and metrics for your meetings and actions
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="include-shared"
              checked={includeShared}
              onCheckedChange={setIncludeShared}
            />
            <Label htmlFor="include-shared">Include shared</Label>
          </div>
          <Select value={period} onValueChange={(value) => setPeriod(value as Period)}>
            <SelectTrigger className="w-[180px] rounded-xl">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">Last Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Insights Panel */}
      {insights.length > 0 && (
        <>
          <InsightsPanel insights={insights} period={period} includeShared={includeShared} />
          <Separator className="my-8" />
        </>
      )}

      {/* Action Health Section */}
      <div className="space-y-6">
        <h2 className="text-3xl font-bold flex items-center gap-2">
          <CheckCircle2 className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
          <span className="bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">
            Action Health Dashboard
          </span>
        </h2>
        
        {/* Hero Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Open Actions"
            value={actionMetrics.openActions}
            tooltip="Total number of action items that haven't been completed yet"
          />
          <MetricCard
            title="Completion Rate"
            value={`${actionMetrics.completionRate}%`}
            tooltip="Percentage of all action items (in this period) that have been completed"
          />
          <MetricCard
            title="Burn Rate"
            value={actionMetrics.burnRate > 0 ? `+${actionMetrics.burnRate}/week` : `${actionMetrics.burnRate}/week`}
            trend={{
              value: actionMetrics.burnRate,
              label: actionMetrics.burnRate > 0 ? 'Reducing backlog' : actionMetrics.burnRate < 0 ? 'Growing backlog' : 'Stable'
            }}
            tooltip="Average net change in actions per week. Positive = reducing backlog (completing more than creating), Negative = growing backlog (creating more than completing)"
          />
          <MetricCard
            title="Oldest Action"
            value={actionMetrics.oldestActionAge === 0 ? 'None' : `${actionMetrics.oldestActionAge} days`}
            tooltip="How long your oldest uncompleted action item has been open"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          <ActionTrendChart data={actionMetrics.trendData} />
          <ActionAgeChart data={actionMetrics.ageDistribution} period={period} includeShared={includeShared} />
        </div>
      </div>

      <Separator className="my-8" />

      {/* Meeting Analytics Section */}
      <div className="space-y-6">
        <h2 className="text-3xl font-bold flex items-center gap-2">
          <CalendarDays className="h-7 w-7 text-violet-600 dark:text-violet-400" />
          <span className="bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">
            Meeting Analytics
          </span>
        </h2>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total Meetings"
            value={meetingMetrics.totalMeetings}
            tooltip="Number of meetings in the selected time period"
          />
          <MetricCard
            title="This Month"
            value={meetingMetrics.meetingsThisMonth}
            trend={{
              value: monthDiff,
              label: monthTrendLabel
            }}
            tooltip="Meetings scheduled this month, with comparison to last month"
          />
          <MetricCard
            title="Avg Actions/Meeting"
            value={meetingMetrics.avgActionsPerMeeting}
            tooltip="Average number of action items created per meeting"
          />
          <MetricCard
            title="Incomplete Agendas"
            value={meetingMetrics.incompleteAgendas}
            tooltip="Meetings with agenda items that haven't been marked complete"
          />
        </div>

        {/* Full-width meeting frequency chart */}
        <MeetingFrequencyChart data={meetingMetrics.dailyFrequency} />
      </div>
    </div>
  );
}
